/**
 * Events as short log lines. Everything that turns a game-core event into text
 * lives here, so the terminal and the browser clients say the same thing. The
 * words are the string table's (`packages/content/strings/<locale>/`):
 * every sentence is a `ui.event.*` key with `{placeholders}` (integration plan
 * slice 5), and the enumerations inside a sentence have their own keys
 * (`ui.dir.*`, `ui.element.*`, `ui.status_name.*`, `ui.stat.*`); a skipped
 * turn is a whole `ui.skip.<reason>` line. Code picks a key, never a word:
 * where English has a conditional clause (`on {targets}`) or an `x{count}`,
 * the key has a variant (`ui.event.skill_used_targets`,
 * `ui.event.item_picked_up_many`). Nothing here decides what happened, it
 * only says it.
 */
import type { FloorDirection } from '@ab/content-schema';
import { type Dir, type Event, type Pos, type RefusalArgs, type RefusalCode, type RunState, type Side, compareUnitIds, dirOffset, dirs } from '@ab/game-core';
import { type Strings, type TextArgs, formatText, joinWords, nameOf } from './text.ts';

/**
 * The letters that tell enemies of one species apart (ADR 0032, Dragon
 * Quest's "Slime A", "Slime B"): Latin capitals in every locale, as Dragon
 * Quest's Japanese has them, so not a string key.
 */
const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** The letter of the `index`-th enemy of a species on a floor (0 is A): A to Z, then AA, AB, ... (a spreadsheet's columns). */
export const enemyLetter = (index: number): string => {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    const r = (n - 1) % letters.length;
    out = (letters[r] ?? '') + out;
    n = (n - 1 - r) / letters.length;
  }
  return out;
};

/**
 * What a run's log remembers from one command to the next (ADR 0032): the
 * letter each enemy was given and which way the dungeon's floors go. Enemies
 * get their letters per species and per floor, in the order they first
 * appear (their `unit_spawned`; one the roster never saw spawn takes the
 * next letter when a namer first knows it, in unit-id order), so "Slime B"
 * stays "Slime B" after Slime A falls. A client keeps one roster per run and
 * passes it to every `describeEvents`; without one, a namer letters the
 * enemies it knows by unit id, which is right until one of a species has
 * fallen.
 */
export type Roster = {
  /** Which way the dungeon's floors go: `ui.floor.down` (B1F) or `ui.floor.up` (1F). */
  readonly direction: FloorDirection;
  /**
   * Notes the floors entered and the enemies spawned in `events`, in order. Idempotent: a unit keeps the letter it
   * has, and only a `floor_entered` of another floor starts the count again (a command's events are described once
   * per locale).
   */
  observe(events: readonly Event[]): void;
  /** The letter of an enemy, given the next free one of its species on this floor if it has none yet. */
  letter(unitId: string, species: string): string;
  /** An enemy the roster has lettered: its species and letter (a namer names it even when no state holds it any more). */
  known(unitId: string): { readonly species: string; readonly letter: string } | undefined;
};

export const createRoster = (direction: FloorDirection = 'down'): Roster => {
  const given = new Map<string, { readonly species: string; readonly letter: string }>();
  let counts = new Map<string, number>();
  let floor: number | null = null;
  const letter = (unitId: string, species: string): string => {
    const known = given.get(unitId);
    if (known !== undefined) return known.letter;
    const n = counts.get(species) ?? 0;
    counts.set(species, n + 1);
    const l = enemyLetter(n);
    given.set(unitId, { species, letter: l });
    return l;
  };
  return {
    direction,
    observe(events) {
      for (const e of events) {
        if (e.type === 'floor_entered' && e.floorNumber !== floor) {
          floor = e.floorNumber;
          counts = new Map();
        } else if (e.type === 'unit_spawned' && e.side === 'enemy' && !e.boss) letter(e.unit, e.monster);
      }
    },
    letter,
    known: (unitId) => given.get(unitId),
  };
};

/** How units and content ids are named in log lines. */
export type Namer = {
  /** The table the names and lines come from. */
  readonly strings: Strings;
  /** The display name of a content id (`monster.slime` to `Slime`), or the id itself. */
  readonly name: (contentId: string) => string;
  /**
   * A unit by id: a party unit by its species name (`ui.event.unit_party_numbered`, "Slime (u002)", when the
   * party has two of that species), an enemy by species and its letter (`ui.event.unit_enemy`, "Slime A": the
   * roster's), the boss by its species name alone (`ui.event.unit_boss`).
   */
  readonly unit: (unitId: string) => string;
  /** A floor's name by the dungeon's direction (`ui.floor.down`, "B1F"; `ui.floor.up`, "1F"). */
  readonly floor: (floorNumber: number) => string;
};

/**
 * A game-core message with every content id in it (`skill.shadow_strike`,
 * `item.x`, `monster.x`, `human.x`, `passive.x`, `dungeon.x`) replaced by its
 * name in the given table (so in that table's language) when it has one; ids
 * it has no name for stay as they are.
 */
export const humanise = (strings: Strings, message: string): string =>
  message.replace(/\b(skill|item|monster|human|passive|dungeon)\.[a-z0-9_]+\b/g, (id) => strings[`${id}.name`] ?? id);

/** A content id as game-core names one in a refusal's args. */
const contentIdPattern = /^(skill|item|monster|human|passive|dungeon)\.[a-z0-9_]+$/;

/**
 * A refused command as one log line in the namer's table: `ui.refusal.<code>`
 * filled from the refusal's args (a unit id through `n.unit`, a content id
 * through the table's names, numbers and raw input as they are). When the
 * table has no line for the code, the English message with its content ids
 * named (`humanise`), so a refusal is never shown as a raw key.
 */
export const describeRefusal = (n: Namer, refusal: { readonly code: RefusalCode; readonly args: RefusalArgs; readonly message: string }): string => {
  const key = `ui.refusal.${refusal.code}`;
  if (n.strings[key] === undefined) return humanise(n.strings, refusal.message);
  const values: Record<string, number | string> = {};
  for (const [name, value] of Object.entries(refusal.args)) {
    if (typeof value === 'number') values[name] = value;
    else if (name === 'unit') values[name] = n.unit(value);
    else values[name] = contentIdPattern.test(value) ? n.name(value) : value;
  }
  return formatText(n.strings, key, { values });
};

/**
 * A namer that knows every unit in `states` and every unit spawned in
 * `events`. Pass the states before and after a command, so units removed by
 * that command (defeated enemies) still have a name, and the run's `roster`
 * (it observes `events` first), so an enemy keeps its letter from command to
 * command; without one, a fresh roster letters the enemies known here.
 */
export const createNamer = (strings: Strings, states: readonly RunState[], events: readonly Event[] = [], roster: Roster = createRoster()): Namer => {
  const known: Record<string, { monster: string; side: Side; boss: boolean }> = {};
  for (const s of states) for (const u of s.units) known[u.id] = { monster: u.monster, side: u.side, boss: u.boss !== null };
  for (const e of events) if (e.type === 'unit_spawned') known[e.unit] = { monster: e.monster, side: e.side, boss: e.boss };
  roster.observe(events);
  // An enemy the roster never saw spawn takes the next letter of its species, in unit-id order (the spawn order).
  for (const id of Object.keys(known).sort(compareUnitIds)) {
    const k = known[id];
    if (k !== undefined && k.side === 'enemy' && !k.boss) roster.letter(id, k.monster);
  }
  /** An enemy no state here holds any more but the roster lettered (it fell in an earlier command). */
  const lettered = (id: string): { monster: string; side: Side; boss: boolean } | undefined => {
    const r = roster.known(id);
    return r === undefined ? undefined : { monster: r.species, side: 'enemy', boss: false };
  };
  const partySpecies = (monster: string): number =>
    Object.entries(known).filter(([, k]) => k.side === 'party' && k.monster === monster).length;
  return {
    strings,
    name: (id) => nameOf(strings, id),
    unit: (id) => {
      const k = known[id] ?? lettered(id);
      if (!k) return id;
      const species = nameOf(strings, k.monster);
      if (k.side === 'enemy') {
        return k.boss
          ? formatText(strings, 'ui.event.unit_boss', { values: { species } })
          : formatText(strings, 'ui.event.unit_enemy', { values: { species, letter: roster.letter(id, k.monster) } });
      }
      return partySpecies(k.monster) > 1 ? formatText(strings, 'ui.event.unit_party_numbered', { values: { species, id } }) : species;
    },
    floor: (floorNumber) => formatText(strings, `ui.floor.${roster.direction}`, { values: { floor: floorNumber } }),
  };
};

/** The direction of a one-tile step, or null for anything else. */
export const stepDir = (from: Pos, to: Pos): Dir | null =>
  dirs.find((d) => dirOffset[d].x === to.x - from.x && dirOffset[d].y === to.y - from.y) ?? null;

/** A stage total as the log shows it: `+2`, `-1`, `0` (a number format, not a word). */
const signed = (n: number): string => (n > 0 ? `+${n}` : String(n));

/** One event as one short line in the namer's table. */
export const describeEvent = (e: Event, n: Namer): string => {
  const line = (name: string, args: TextArgs = {}): string => formatText(n.strings, `ui.event.${name}`, args);
  /** A line about one unit: `{unit}` plus the given values. */
  const about = (name: string, unit: string, values: Readonly<Record<string, number | string>> = {}, keys: Readonly<Record<string, string>> = {}): string =>
    line(name, { values: { unit: n.unit(unit), ...values }, keys });
  switch (e.type) {
    case 'floor_entered':
      return line('floor_entered', { values: { floor: n.floor(e.floorNumber) } });
    case 'unit_spawned':
      return about(e.side === 'party' ? 'unit_spawned_party' : 'unit_spawned_enemy', e.unit, { level: e.level });
    case 'turned':
      return about('turned', e.unit, {}, { dir: `ui.dir.${e.facing}` });
    case 'moved': {
      // A one-tile move is a step; anything longer is a reposition (Shadow Strike).
      const d = stepDir(e.from, e.to);
      return d === null ? about('leaped', e.unit, { x: e.to.x, y: e.to.y }) : about('moved', e.unit, {}, { dir: `ui.dir.${d}` });
    }
    case 'attacked':
      return e.target === null ? about('attacked_air', e.unit) : about('attacked', e.unit, { target: n.unit(e.target) });
    case 'skill_used':
      return e.targets.length
        ? about('skill_used_targets', e.unit, { skill: n.name(e.skill), targets: joinWords(n.strings, e.targets.map(n.unit)) })
        : about('skill_used', e.unit, { skill: n.name(e.skill) });
    case 'item_used':
      return e.targets.length
        ? about('item_used_targets', e.unit, { item: n.name(e.item), targets: joinWords(n.strings, e.targets.map(n.unit)) })
        : about('item_used', e.unit, { item: n.name(e.item) });
    case 'item_picked_up':
      return about(e.count > 1 ? 'item_picked_up_many' : 'item_picked_up', e.unit, { item: n.name(e.item), count: e.count });
    case 'item_left':
      return line(e.count > 1 ? 'item_left_many' : 'item_left', { values: { item: n.name(e.item), count: e.count } });
    case 'missed':
      return about('missed', e.unit, { target: n.unit(e.target) });
    case 'damaged':
      if (e.cause === 'burn') return about('damaged_burn', e.unit, { amount: e.amount, hp: e.hp });
      if (e.cause === 'poison') return about('damaged_poison', e.unit, { amount: e.amount, hp: e.hp });
      return e.element === null
        ? about('damaged', e.unit, { amount: e.amount, hp: e.hp })
        : about('damaged_element', e.unit, { amount: e.amount, hp: e.hp }, { element: `ui.element.${e.element}` });
    case 'healed':
      if (e.amount === 0) return about('healed_full', e.unit);
      if (e.cause === 'regen') return about('healed_regen', e.unit, { amount: e.amount, hp: e.hp });
      if (e.cause === 'sleep') return about('healed_sleep', e.unit, { amount: e.amount, hp: e.hp });
      return about('healed', e.unit, { amount: e.amount, hp: e.hp });
    case 'ap_changed':
      return about(e.delta >= 0 ? 'ap_gained' : 'ap_spent', e.unit, { amount: Math.abs(e.delta), ap: e.ap });
    case 'spring_used':
      return about('spring_used', e.unit, { percent: e.percent });
    case 'sp_changed':
      return about(e.delta >= 0 ? 'sp_gained' : 'sp_lost', e.unit, { amount: Math.abs(e.delta), sp: e.sp });
    case 'status_applied':
      return about('status_applied', e.unit, {}, { status: `ui.status_name.${e.status}` });
    case 'status_cured':
      return about('status_cured', e.unit, {}, { status: `ui.status_name.${e.status}` });
    case 'status_expired':
      return about('status_expired', e.unit, {}, { status: `ui.status_name.${e.status}` });
    case 'stage_changed':
      return about('stage_changed', e.unit, { stages: signed(e.stages) }, { stat: `ui.stat.${e.stat}` });
    case 'charge_started':
      return about('charge_started', e.unit, { skill: n.name(e.skill) });
    case 'charge_fired':
      return about('charge_fired', e.unit, { skill: n.name(e.skill) });
    case 'charge_interrupted':
      return about('charge_interrupted', e.unit, { skill: n.name(e.skill) });
    case 'passive_triggered':
      return about('passive_triggered', e.unit, { passive: n.name(e.passive) });
    case 'action_skipped':
      return formatText(n.strings, `ui.skip.${e.reason}`, { values: { unit: n.unit(e.unit) } });
    case 'woke_up':
      return about('woke_up', e.unit);
    case 'unit_defeated':
      return about('unit_defeated', e.unit);
    case 'item_auto_used':
      return about('item_auto_used', e.unit, { item: n.name(e.item) });
    case 'revived':
      return about('revived', e.unit, { hp: e.hp });
    case 'boss_defeated':
      return about('boss_defeated', e.unit);
    case 'egg_obtained':
      return line('egg_obtained', { values: { monster: n.name(e.monster) } });
    case 'exp_gained':
      return about('exp_gained', e.unit, { amount: e.amount });
    case 'level_up':
      return about('level_up', e.unit, { level: e.level });
    case 'skill_learned':
      return about('skill_learned', e.unit, { skill: n.name(e.skill) });
    case 'escaped':
      return about('escaped', e.unit);
    case 'round_ended':
      return line('round_ended', { values: { turn: e.turn } });
    case 'floor_cleared':
      return line('floor_cleared', { values: { floor: n.floor(e.floorNumber) } });
    case 'run_completed':
      return line('run_completed');
    case 'run_failed':
      return line('run_failed');
    case 'items_lost':
      return e.items.length === 0
        ? line('items_lost_none')
        : line('items_lost', {
            values: {
              items: joinWords(
                n.strings,
                e.items.map((i) => formatText(n.strings, i.count > 1 ? 'ui.event.item_stack_many' : 'ui.event.item_stack', { values: { item: n.name(i.item), count: i.count } })),
              ),
            },
          });
    case 'run_escaped':
      return line('run_escaped');
  }
};

/**
 * The events of one command as lines, named against the states before and
 * after it. A party unit's `moved` onto an AP spring that is still unspent
 * after the command adds a `ui.event.spring_stepped` line (enemies never
 * drink, and a spring drunk in the same command, by a wait or by an ally the
 * leader swapped onto it, gets `spring_used` instead); whether and when the
 * spring is used is game-core's `spring_used`. A charm's `item_auto_used`
 * followed by the `revived` of the same unit is one line,
 * `ui.event.shatter_revived` ("The Revive Orb shatters: Brad is back with 20 HP.").
 * Pass the run's `roster` (`Roster`) so enemies keep their letters across
 * commands and floors are named by the dungeon's direction.
 */
export const describeEvents = (strings: Strings, events: readonly Event[], before: RunState, after: RunState, roster?: Roster): string[] => {
  const namer = createNamer(strings, [before, after], events, roster);
  const springs = after.floor?.springs ?? [];
  const party = (id: string): boolean => (after.units.find((u) => u.id === id) ?? before.units.find((u) => u.id === id))?.side === 'party';
  return events.flatMap((e, i) => {
    const prev = events[i - 1];
    if (e.type === 'revived' && prev?.type === 'item_auto_used' && prev.unit === e.unit) return [];
    const next = events[i + 1];
    if (e.type === 'item_auto_used' && next?.type === 'revived' && next.unit === e.unit) {
      return [formatText(strings, 'ui.event.shatter_revived', { values: { item: namer.name(e.item), unit: namer.unit(e.unit), hp: next.hp } })];
    }
    const line = describeEvent(e, namer);
    const onSpring = e.type === 'moved' && party(e.unit) && springs.some((s) => s.pos.x === e.to.x && s.pos.y === e.to.y);
    return onSpring ? [line, formatText(strings, 'ui.event.spring_stepped', { values: { unit: namer.unit(e.unit) } })] : [line];
  });
};
